import { IActionDefinitionDto, IActionParameter } from "@kuika/kuika-cl-model";
import { filterActionDefinitions } from "./action-definition-filter";
import { getDesignerActionParameterEditorConfig } from "./designer-action-parameter-editor-actions";
import type { ScreenDesignInstance } from "./designer-context";

interface ClassifiedParam {
  name: string;
  declaredType: string;
  inputType: string;
  options?: Array<{ label: string; value: string }>;
  dynamicLookup?: string;
  hint?: string;
  autoValue?: string;
}

// Auto-skip internal parameters
export const AUTO_SET_PARAMS: Record<string, string> = {
  executePageInitActionsAfterClose: "true",
  disableSmartExecutePageInit: "false",
  fromScreenId: "__CURRENT_SCREEN_ID__",
  navigationKuikaId: "",
  warnLandscape: "false",
  triggerParentAppGoBack: "false",
  multipleValuePropName: ""
};

// Known enum mappings by parameter name
const KNOWN_ENUMS: Record<string, Array<{ label: string; value: string }>> = {
  visibilityType: [
    { label: "Visible", value: "Visible" },
    { label: "Hidden", value: "Hidden" },
    { label: "Toggle", value: "Toggle" }
  ],
  editabilityType: [
    { label: "Enabled", value: "Enabled" },
    { label: "Disabled", value: "Disabled" },
    { label: "Toggle", value: "Toggle" }
  ],
  openAs: [
    { label: "New Tab", value: "NewTab" },
    { label: "Current Page", value: "CurrentPage" },
    { label: "Modal", value: "Modal" },
    { label: "Drawer", value: "Drawer" }
  ],
  placemet: [
    { label: "Right", value: "Right" },
    { label: "Left", value: "Left" },
    { label: "Top", value: "Top" },
    { label: "Bottom", value: "Bottom" }
  ]
};

// Context-sensitive enum mappings (by action name + param name)
const ACTION_SPECIFIC_ENUMS: Record<string, Record<string, Array<{ label: string; value: string }>>> = {
  Notify: {
    type: [
      { label: "Default", value: "Default" },
      { label: "Info", value: "Info" },
      { label: "Success", value: "Success" },
      { label: "Warning", value: "Warning" },
      { label: "Danger", value: "Danger" }
    ],
    position: [
      { label: "Top", value: "Top" },
      { label: "Center", value: "Center" },
      { label: "Bottom", value: "Bottom" }
    ]
  },
  ComponentToPdf: {
    quality: [
      { label: "Low", value: "Low" },
      { label: "Medium", value: "Medium" },
      { label: "High", value: "High" }
    ],
    orientation: [
      { label: "Portrait", value: "Portrait" },
      { label: "Landscape", value: "Landscape" }
    ]
  },
  ComponentToPdf2: {
    quality: [
      { label: "Low", value: "Low" },
      { label: "Medium", value: "Medium" },
      { label: "High", value: "High" }
    ],
    orientation: [
      { label: "Portrait", value: "Portrait" },
      { label: "Landscape", value: "Landscape" }
    ]
  },
  PhotoTake: {
    quality: [
      { label: "Default", value: "Default" },
      { label: "Low", value: "Low" },
      { label: "Medium", value: "Medium" },
      { label: "High", value: "High" }
    ]
  },
  PhotoFromGallery: {
    quality: [
      { label: "Default", value: "Default" },
      { label: "Low", value: "Low" },
      { label: "Medium", value: "Medium" },
      { label: "High", value: "High" }
    ]
  },
  Confirmation: {
    confirmationIcon: [
      { label: "Warning", value: "warning" },
      { label: "Info", value: "info" },
      { label: "Error", value: "error" },
      { label: "Success", value: "check_circle" },
      { label: "Help", value: "help" },
      { label: "Delete", value: "delete" }
    ]
  }
};

// Known enum mappings by parameter name (extra fallback)
const KNOWN_ENUMS_EXTRA: Record<string, Array<{ label: string; value: string }>> = {
  quality: [
    { label: "Default", value: "Default" },
    { label: "Low", value: "Low" },
    { label: "Medium", value: "Medium" },
    { label: "High", value: "High" }
  ],
  orientation: [
    { label: "Portrait", value: "Portrait" },
    { label: "Landscape", value: "Landscape" }
  ]
};

// Known type-based enum mappings
const TYPE_ENUMS: Record<string, Array<{ label: string; value: string }>> = {
  EPlatformType: [
    { label: "Web", value: "Web" },
    { label: "Mobile", value: "Mobile" },
    { label: "All", value: "All" }
  ],
  EChangeTwoFactorAuthenticationType: [
    { label: "Email", value: "Email" },
    { label: "SMS", value: "SMS" },
    { label: "Authenticator", value: "Authenticator" },
    { label: "Disable", value: "Disable" }
  ]
};

function classifyParameter(param: IActionParameter, actionName: string): ClassifiedParam & { isAuto: boolean } {
  const pName = param.name;
  const pNameLower = pName.toLowerCase();
  const pType = param.Typename || "String";

  // 1. Auto-skip internal params
  if (pName in AUTO_SET_PARAMS) {
    return {
      name: pName,
      declaredType: pType,
      inputType: "auto",
      autoValue: AUTO_SET_PARAMS[pName],
      isAuto: true
    };
  }

  // 2. Screen selection (name contains "screenId" case-insensitive)
  if (pNameLower.includes("screenid") && pNameLower !== "fromscreenid") {
    return {
      name: pName,
      declaredType: pType,
      inputType: "screen_select",
      dynamicLookup: "ui_common_list_screens",
      hint: "Call ui_common_list_screens, present screen names as options",
      isAuto: false
    };
  }

  // 3. Component selection
  if (
    pNameLower === "componentname" ||
    pNameLower === "targetdiname" ||
    pNameLower === "diid" ||
    pNameLower === "targetdiid"
  ) {
    return {
      name: pName,
      declaredType: pType,
      inputType: "component_select",
      dynamicLookup: "ui_common_get_screen_tree",
      hint: "Call ui_common_get_screen_tree, present component names as options",
      isAuto: false
    };
  }

  // 4. Native enum detection (from runtime param metadata)
  if (param.isEnum && param.enumTypeValues && param.enumTypeValues.length > 0) {
    return {
      name: pName,
      declaredType: pType,
      inputType: "enum",
      options: param.enumTypeValues.map((v: string) => ({ label: v, value: v })),
      isAuto: false
    };
  }

  // 5. Action-specific enums (e.g. Notify -> type, position)
  const actionSpecific = ACTION_SPECIFIC_ENUMS[actionName];
  if (actionSpecific && actionSpecific[pName]) {
    return {
      name: pName,
      declaredType: pType,
      inputType: "enum",
      options: actionSpecific[pName],
      isAuto: false
    };
  }

  // 6. Known enums by parameter name
  if (KNOWN_ENUMS[pName]) {
    return {
      name: pName,
      declaredType: pType,
      inputType: "enum",
      options: KNOWN_ENUMS[pName],
      isAuto: false
    };
  }

  // 6b. Known enums by parameter name (extra fallback)
  if (KNOWN_ENUMS_EXTRA[pName]) {
    return {
      name: pName,
      declaredType: pType,
      inputType: "enum",
      options: KNOWN_ENUMS_EXTRA[pName],
      isAuto: false
    };
  }

  // 7. Known type-based enums
  if (TYPE_ENUMS[pType]) {
    return {
      name: pName,
      declaredType: pType,
      inputType: "enum",
      options: TYPE_ENUMS[pType],
      isAuto: false
    };
  }

  // 8. Type-based fallbacks
  if (pType === "Boolean") {
    return {
      name: pName,
      declaredType: pType,
      inputType: "boolean",
      options: [
        { label: "true", value: "true" },
        { label: "false", value: "false" }
      ],
      isAuto: false
    };
  }

  if (["Int32", "Int64", "Double", "Decimal"].includes(pType)) {
    return {
      name: pName,
      declaredType: pType,
      inputType: "number",
      isAuto: false
    };
  }

  if (pType === "DateTimeOffset") {
    return {
      name: pName,
      declaredType: pType,
      inputType: "date",
      isAuto: false
    };
  }

  // 9. Default: text
  return {
    name: pName,
    declaredType: pType,
    inputType: "text",
    isAuto: false
  };
}

export function getDesignerActionParameterOptions(
  sd: ScreenDesignInstance,
  input: { actionDefinitionId?: string }
): Record<string, unknown> {
  const { actionDefinitionId } = input;
  if (!actionDefinitionId) {
    return { error: "actionDefinitionId is required" };
  }

  const rawDefs: IActionDefinitionDto[] | undefined = sd.projectProvider.state.actionDefinitions;
  if (!rawDefs || rawDefs.length === 0) {
    return { error: "Action definitions not loaded" };
  }
  const allDefs = filterActionDefinitions(rawDefs, sd);

  // Look up by ID or name (case-insensitive)
  const idUpper = actionDefinitionId.toUpperCase();
  let entry: IActionDefinitionDto | undefined;

  // Try exact ID match
  entry = allDefs.find((a) => a.id === actionDefinitionId);

  // Try case-insensitive name match
  if (!entry) {
    entry = allDefs.find((a) => a.name?.toUpperCase() === idUpper);
  }

  // Try GUID match (upper-cased)
  if (!entry) {
    entry = allDefs.find((a) => a.id?.toUpperCase() === idUpper);
  }

  if (!entry) {
    return {
      note: "Not in system catalog. May be a datasource action. Use common.get_action_detail(actionId) to inspect parameters.",
      interactiveParameters: [],
      autoSetParameters: []
    };
  }

  const actionParams: IActionParameter[] = entry.actionParameters || [];
  const interactiveParameters: ClassifiedParam[] = [];
  const autoSetParameters: ClassifiedParam[] = [];

  // Create a stub action object for getDesignerActionParameterEditorConfig
  const stubAction = { id: "", parameters: actionParams, label: "", description: "" };

  for (const param of actionParams) {
    // Check if the designer has an exclusive editor config for this parameter
    const editorConfig = getDesignerActionParameterEditorConfig(sd, stubAction, entry, param);
    if (editorConfig && editorConfig.options && editorConfig.options.length > 0) {
      // Use the editor config options
      interactiveParameters.push({
        name: param.name,
        declaredType: param.Typename || "String",
        inputType: editorConfig.isMultiSelect ? "multi_select" : "enum",
        options: editorConfig.options.map((o) => ({
          label: o.label,
          value: String(o.value)
        }))
      });
      continue;
    }

    const classified = classifyParameter(param, entry.name);
    if (classified.isAuto) {
      autoSetParameters.push({
        name: classified.name,
        declaredType: classified.declaredType,
        inputType: classified.inputType,
        autoValue: classified.autoValue
      });
    } else {
      const interactive: ClassifiedParam = {
        name: classified.name,
        declaredType: classified.declaredType,
        inputType: classified.inputType
      };
      if (classified.options) interactive.options = classified.options;
      if (classified.dynamicLookup) interactive.dynamicLookup = classified.dynamicLookup;
      if (classified.hint) interactive.hint = classified.hint;
      interactiveParameters.push(interactive);
    }
  }

  return {
    actionName: entry.name,
    actionId: entry.id,
    categoryName: entry.actionCategory != null ? String(entry.actionCategory) : null,
    purpose: null,
    totalParameters: actionParams.length,
    interactiveParameters,
    autoSetParameters
  };
}
